
import apiClient from './client'


export interface Collections {
    user_id: number;
    name: string;
    description: string;
    language: string;
    language_level: string;
    collection_type: string;
    tags: number[];
    image_url?: string;
    is_public: boolean;
}

/** Response from POST /api/v1/collections (includes generated id) */
export interface CollectionCreated extends Collections {
    id: number;
}

export interface CollectionList {
    collections: CollectionItem[];
    pagination: {
        page: number;
        pageSize: number;
        total: number;
    };
}
export interface CollectionItem {
    id: number;
    user_id: number;
    name: string;
    description: string;
    language: string;
    language_level: string;
    collection_type: string;
    tags: number[];
    image_url?: string;
    is_public: boolean;
    item_count: number;
    mastered_count: number;
    learning_count: number;
    /** Set when this collection was cloned from a starter pack — starter clones can't be shared. */
    source_starter_id?: number;
}

export interface ShareTokenResponse {
    share_token: string;
    share_url: string;
}

export interface StarterCollectionItem {
    id: number;
    name: string;
    description: string;
    language: string;
    collection_type: string;
    tags: number[];
    image_url?: string;
    item_count: number;
    already_cloned?: boolean;
}

/** The anonymous teaser for a share-token link (GET /api/collections/share/:token). */
export interface PublicSharedCollection {
    name: string;
    description: string;
    language: string;
    language_level: string;
    collection_type: string;
    item_count: number;
    sample_words?: string[];
}

/** A legacy is_public collection with its full word list (GET /api/collections/:id/public). */
export interface LegacyPublicCollection {
    name: string;
    description: string;
    language: string;
    language_level: string;
    collection_type: string;
    item_count: number;
    vocabulary_items?: { id: number; word: string; translation: string[] }[];
}

/** The full word list behind a share token, for signed-in viewers (GET /api/v1/collections/share/:token/full). */
export interface FullSharedCollection {
    name: string;
    description: string;
    language: string;
    language_level: string;
    collection_type: string;
    item_count: number;
    words: { word: string; translation?: string[] }[];
}

export const collectionService = {
    // Public, no auth — backs PublicCollectionPage.tsx for share-token links.
    getPublicByShareToken: async (token: string): Promise<PublicSharedCollection> => {
        const response = await apiClient.getPublic(`/api/collections/share/${encodeURIComponent(token)}`);
        return response.data as PublicSharedCollection;
    },

    // Public, no auth — the legacy /c/<numeric id> links (is_public collections, still in the sitemap).
    getLegacyPublic: async (id: string): Promise<LegacyPublicCollection> => {
        const response = await apiClient.getPublic(`/api/collections/${encodeURIComponent(id)}/public`);
        return response.data as LegacyPublicCollection;
    },

    // Auth — whether the caller already imported the collection behind a share token.
    getShareImportStatus: async (token: string): Promise<{ already_imported: boolean }> => {
        const response = await apiClient.get(`/api/v1/collections/share/${encodeURIComponent(token)}/status`);
        return response.data as { already_imported: boolean };
    },

    // Auth — the full word list behind a share token (anonymous visitors only get the teaser).
    getFullByShareToken: async (token: string): Promise<FullSharedCollection> => {
        const response = await apiClient.get(`/api/v1/collections/share/${encodeURIComponent(token)}/full`);
        return response.data as FullSharedCollection;
    },

    addCollection: async (collection: Collections): Promise<CollectionCreated> => {

        try {
            const response = await apiClient.post(`/api/v1/collections`, collection);
            return response.data;
        } catch (error) {
            console.error(error);
            throw new Error('Failed to add collection');
        }
    },

    getCollection: async (pagination: any, filters: any) => {
        let url = '/api/v1/collections';
        const params: Record<string, string | number | undefined> = {
            pageSize: 1000,
        };

        // Add filter parameters if provided
        if (filters) {
            if (filters.search) params.search = filters.search;
            if (filters.language) params.language = filters.language;
            if (filters.languageLevel) params.collectionId = filters.languageLevel;
            if (filters.collectionType) params.collectionId = filters.collectionType;
            if (filters.collectionId) params.collectionId = filters.collectionId;
            if (filters.tags && filters.tags.length > 0) {
                params.tags = filters.tags.join(',');
            }
        }

        const response = await apiClient.get(url, { params });
        return response.data.collections;
    },
    getCollectionById: async (collectionId: any) => {
        let url = `/api/v1/collections/${collectionId}`;
        const params: Record<string, string | number | undefined> = {};
        const response = await apiClient.get(url, { params });
        return response.data
    },
    getAllCollection: async (filters: any) => {
        let url = '/api/v1/collections';
        const params: Record<string, string | number | undefined> = {
            pageSize: 1000,
        };

        // Add filter parameters if provided
        if (filters) {
            if (filters.search) params.search = filters.search;
            if (filters.language) params.language = filters.language;
            if (filters.languageLevel) params.collectionId = filters.languageLevel;
            if (filters.collectionType) params.collectionId = filters.collectionType;
            if (filters.collectionId) params.collectionId = filters.collectionId;
            if (filters.tags && filters.tags.length > 0) {
                params.tags = filters.tags.join(',');
            }
        }

        const response = await apiClient.get(url, { params });
        return response.data;

    },
    updateCollection: async (id: number, collection: Collections) => {
        try {
            const response = await apiClient.put(`/api/v1/collections/${id}`, collection);
            return response.data;
        } catch (error) {
            console.error(error);
            throw new Error('Failed to update collection');
        }
    },

    deleteCollection: async (id: number) => {
        try {
            const response = await apiClient.delete(`/api/v1/collections/${id}`);
            return response.data;
        } catch (error) {
            console.error(error);
            throw new Error('Failed to delete collection');
        }
    },

    // Mint (or fetch the existing) public share link for this collection — the public
    // page it points to is already live at /c/:token (see PublicCollectionPage.tsx).
    // 422s for starter-collection clones (ErrStarterCollectionNotShareable server-side).
    getShareLink: async (id: number): Promise<ShareTokenResponse> => {
        const response = await apiClient.post(`/api/v1/collections/${id}/share`);
        return response.data as ShareTokenResponse;
    },

    getStarters: async (language: string, translationLanguage?: string): Promise<StarterCollectionItem[]> => {
        const params: Record<string, string> = { language };
        if (translationLanguage) params.translation_language = translationLanguage;
        const response = await apiClient.get('/api/v1/collections/starters', { params });
        // Already sorted A1 → C2 by the API (CollectionService.GetStarters).
        return response.data as StarterCollectionItem[];
    },

    // Deep-copies a shared collection (found at /c/:token) into the caller's account —
    // backs the "Copy to my account" action on PublicCollectionPage.tsx for logged-in visitors.
    importByToken: async (token: string): Promise<CollectionCreated> => {
        const response = await apiClient.post(`/api/v1/collections/share/${token}/import`, undefined, { timeoutMs: 30_000 });
        return response.data as CollectionCreated;
    },

    cloneStarterCollection: async (starterId: number, translationLanguage?: string): Promise<{ collection_id: number; skipped?: number }> => {
        // Server inserts the starter's words + SR records synchronously
        // before responding (can be thousands of rows for the largest
        // packs) — the default 10s client timeout can fire before that completes.
        const response = await apiClient.post(`/api/v1/collections/${starterId}/clone`, undefined, {
            params: { translation_language: translationLanguage },
            timeoutMs: 30_000,
        });
        return response.data;
    },
};